import React, { useState, useEffect, FormEvent } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FrontendLayout from "../../components/frontend/FrontendLayout";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

// Define user types for clarity and consistency
enum UserType {
  Owner = 1,
  Agent = 2,
  Buyer = 3,
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const LoginRegister: React.FC = () => {
  const navigate = useNavigate();

  // Login State
  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
    usertype: UserType.Buyer,
  });
  const [loginErrors, setLoginErrors] = useState<{ [key: string]: string }>({});
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginCaptchaImage, setLoginCaptchaImage] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);

  // Register State
  const [registerFormData, setRegisterFormData] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    usertype: UserType.Owner,
    agreeToTerms: false,
  });
  const [registerErrors, setRegisterErrors] = useState<{
    [key: string]: string;
  }>({});
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerCaptchaImage, setRegisterCaptchaImage] = useState("");
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState<number | null>(null);

  // OTP Modal State
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    setLoginErrors((prev) => ({ ...prev, captcha: "" }));
  };

  // Captcha Generation
  const generateCaptcha = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 5; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return result;
  };

  const [currentLoginCaptcha, setCurrentLoginCaptcha] = useState("");
  const [currentRegisterCaptcha, setCurrentRegisterCaptcha] = useState("");

  useEffect(() => {
    // const loginCap = generateCaptcha();
    // setCurrentLoginCaptcha(loginCap);
    // setLoginCaptchaImage(
    //   `https://dummyimage.com/80x35/ccc/000&text=${loginCap}`
    // );

    const registerCap = generateCaptcha();
    setCurrentRegisterCaptcha(registerCap);
    setRegisterCaptchaImage(
      `https://dummyimage.com/80x35/ccc/000&text=${registerCap}`
    );
  }, []);

  const toggleFade = () => {
    setShowLogin((prev) => !prev);
    // Regenerate captchas
    // const newLoginCap = generateCaptcha();
    // setCurrentLoginCaptcha(newLoginCap);
    // setLoginCaptchaImage(
    //   `https://dummyimage.com/80x35/ccc/000&text=${newLoginCap}`
    // );

    const newRegisterCap = generateCaptcha();
    setCurrentRegisterCaptcha(newRegisterCap);
    setRegisterCaptchaImage(
      `https://dummyimage.com/80x35/ccc/000&text=${newRegisterCap}`
    );

    // Reset form data and errors
    setLoginFormData({
      email: "",
      password: "",
      usertype: UserType.Owner,
    });
    setLoginErrors({});
    setLoginLoading(false);
    setShowLoginPassword(false);

    setRegisterFormData({
      name: "",
      email: "",
      password: "",
      mobile: "",
      usertype: UserType.Owner,
      agreeToTerms: false,
    });
    setRegisterErrors({});
    setRegisterLoading(false);
    setShowRegisterPassword(false);
    setShowOtpModal(false);
    setOtp("");
    setOtpError("");
  };

  // Login Handlers
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginFormData((prev) => ({ ...prev, [name]: value }));
    setLoginErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLoginUserTypeChange = (usertype: UserType) => {
    setLoginFormData((prev) => ({ ...prev, usertype }));
  };

  const validateLogin = () => {
    const newErrors: { [key: string]: string } = {};
    let isValid = true;

    if (!loginFormData.email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(loginFormData.email)) {
      newErrors.email = "Email is invalid.";
      isValid = false;
    }

    if (!loginFormData.password.trim()) {
      newErrors.password = "Password is required.";
      isValid = false;
    }

    // if (!loginFormData.captcha.trim()) {
    //   newErrors.captcha = "Captcha is required.";
    //   isValid = false;
    // } else if (loginFormData.captcha !== currentLoginCaptcha) {
    //   newErrors.captcha = "Invalid captcha.";
    //   isValid = false;
    // }

    if (!recaptchaValue) {
      newErrors.captcha = "Please complete the reCAPTCHA.";
      isValid = false;
    }

    setLoginErrors(newErrors);
    return isValid;
  };

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateLogin()) {
      return;
    }

    setLoginLoading(true);
    try {
      const payload = {
        email: loginFormData.email,
        password: loginFormData.password,
        us_usertype: loginFormData.usertype,
      };
      const res = await axios.post(`${API_BASE}/api/authuser/login`, payload);

      if (res.data.success) {
        toast.success(res.data.message);
        localStorage.setItem("frontendUserToken", res.data.token);
        localStorage.setItem("frontendUserId", res.data.profile.id);
        localStorage.setItem("frontendUserType", res.data.profile.usertype);
        localStorage.setItem("frontendUserName", res.data.profile.name);
        localStorage.setItem("frontendUserEmail", res.data.profile.email);
        localStorage.setItem("frontendUserMobile", res.data.profile.mobile);
        localStorage.setItem("frontendUserIp", res.data.profile.clientIp);

        window.dispatchEvent(new Event("loginStatusChanged"));

        const returnUrl = localStorage.getItem("returnUrl");
        if (returnUrl) {
          const hashRoute = new URL(returnUrl).hash.replace("#", "");
          localStorage.removeItem("returnUrl");
          navigate(hashRoute || "/authuser/dashboard");
        } else {
          navigate("/authuser/dashboard");
        }
      } else {
        toast.error(res.data.message || "Login failed. Please try again.");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "An unexpected error occurred during login.";
      toast.error(errorMessage);
      console.error("Login Error:", error);
    } finally {
      setLoginLoading(false);
      const newCap = generateCaptcha();
      setCurrentLoginCaptcha(newCap);
      setLoginCaptchaImage(
        `https://dummyimage.com/80x35/ccc/000&text=${newCap}`
      );
    }
  };

  // Register Handlers
  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setRegisterFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setRegisterErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRegisterUserTypeChange = (usertype: UserType) => {
    setRegisterFormData((prev) => ({ ...prev, usertype }));
  };

  const validateRegister = () => {
    const newErrors: { [key: string]: string } = {};
    let isValid = true;

    if (!registerFormData.name.trim()) {
      newErrors.name = "Name is required.";
      isValid = false;
    } else if (registerFormData.name.length > 100) {
      newErrors.name = "Name must not exceed 100 characters.";
      isValid = false;
    }

    if (!registerFormData.email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(registerFormData.email)) {
      newErrors.email = "Email is invalid.";
      isValid = false;
    } else if (registerFormData.email.length > 100) {
      newErrors.email = "Email must not exceed 100 characters.";
      isValid = false;
    }

    if (!registerFormData.password.trim()) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else if (registerFormData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      isValid = false;
    } else if (registerFormData.password.length > 100) {
      newErrors.password = "Password must not exceed 100 characters.";
      isValid = false;
    }

    if (!registerFormData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required.";
      isValid = false;
    } else if (!/^\d{10}$/.test(registerFormData.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits.";
      isValid = false;
    }

    if (!registerFormData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions.";
      isValid = false;
    }

    if (!recaptchaValue) {
      newErrors.captcha = "Please complete the reCAPTCHA.";
      isValid = false;
    }

    // if (!registerFormData.captcha.trim()) {
    //   newErrors.captcha = "Captcha is required.";
    //   isValid = false;
    // } else if (registerFormData.captcha !== currentRegisterCaptcha) {
    //   newErrors.captcha = "Invalid captcha.";
    //   isValid = false;
    // }

    setRegisterErrors(newErrors);
    return isValid;
  };

  const handleRegisterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateRegister()) {
      return;
    }

    setRegisterLoading(true);
    try {
      const payload = {
        us_name: registerFormData.name,
        us_email: registerFormData.email,
        us_password: registerFormData.password,
        us_mobile: registerFormData.mobile,
        us_usertype: registerFormData.usertype,
      };
      const res = await axios.post(
        `${API_BASE}/api/authuser/register`,
        payload
      );

      if (res.data.success) {
        toast.success(res.data.message);
        setRegisteredUserId(res.data.userId);
        setShowOtpModal(true);
      } else {
        toast.error(
          res.data.message || "Registration failed. Please try again."
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "An unexpected error occurred during registration.";
      toast.error(errorMessage);
      console.error("Registration Error:", error);
    } finally {
      setRegisterLoading(false);
      const newCap = generateCaptcha();
      setCurrentRegisterCaptcha(newCap);
      setRegisterCaptchaImage(
        `https://dummyimage.com/80x35/ccc/000&text=${newCap}`
      );
    }
  };

  // OTP Handlers
  const handleOtpDigitChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value;
    setOtpDigits(newOtpDigits);
    setOtpError("");

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    setOtp(newOtpDigits.join(""));
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setOtpError("OTP is required.");
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("OTP must be 6 digits.");
      return;
    }

    if (registeredUserId === null) {
      toast.error(
        "User ID not found for OTP verification. Please re-register."
      );
      setShowOtpModal(false);
      return;
    }

    setOtpLoading(true);
    try {
      const payload = {
        userId: registeredUserId,
        otp: parseInt(otp, 10),
      };
      const res = await axios.post(
        `${API_BASE}/api/authuser/verify-otp`,
        payload
      );

      if (res.data.success) {
        toast.success(res.data.message || "OTP verified successfully!");
        setShowOtpModal(false);
        setShowLogin(true); // Switch to login
        setLoginFormData((prev) => ({
          ...prev,
          email: registerFormData.email,
        }));
        setOtp("");
        setRegisteredUserId(null);
      } else {
        toast.error(
          res.data.message || "OTP verification failed. Please try again."
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "An unexpected error occurred during OTP verification.";
      toast.error(errorMessage);
      console.error("OTP Verification Error:", error);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <FrontendLayout>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="main-content">
        <div className="container-fluid" style={{ background: "#fef7f1" }}>
          <div className="d-flex justify-content-center p-md-5 pb-0">
            <div className="container2" id="container">
              <div id="div1" className={`fade-div ${showLogin ? "show" : ""}`}>
                <div className="login-box form-contact form">
                  <div className="row">
                    <div className="col-md-6 left-section d-none d-sm-block"></div>
                    <div className="col-md-6">
                      <div className="p-md-5">
                        <h5 className="mb-4 title text-center">Login</h5>
                        <hr />
                        <label className="form-label">Are you</label>
                        <ul
                          className="nav nav-tabs mb-3 border-0"
                          id="loginTab"
                          role="tablist"
                        >
                          <li className="nav-item pe-2" role="presentation">
                            <button
                              className={`nav-link ${
                                loginFormData.usertype === UserType.Owner
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleLoginUserTypeChange(UserType.Owner)
                              }
                              type="button"
                              role="tab"
                            >
                              Owner
                            </button>
                          </li>
                          <li className="nav-item pe-2" role="presentation">
                            <button
                              className={`nav-link ${
                                loginFormData.usertype === UserType.Agent
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleLoginUserTypeChange(UserType.Agent)
                              }
                              type="button"
                              role="tab"
                            >
                              Agent
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button
                              className={`nav-link ${
                                loginFormData.usertype === UserType.Buyer
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleLoginUserTypeChange(UserType.Buyer)
                              }
                              type="button"
                              role="tab"
                            >
                              Buyer
                            </button>
                          </li>
                        </ul>
                        <form onSubmit={handleLoginSubmit}>
                          <div className="mb-3">
                            <label htmlFor="loginEmail" className="form-label">
                              Email address
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="email"
                              className={`form-control ${
                                loginErrors.email ? "is-invalid" : ""
                              }`}
                              id="loginEmail"
                              name="email"
                              placeholder="name@example.com"
                              value={loginFormData.email}
                              onChange={handleLoginChange}
                            />
                            {loginErrors.email && (
                              <div className="invalid-feedback">
                                {loginErrors.email}
                              </div>
                            )}
                          </div>
                          <div className="mb-3 position-relative">
                            <label
                              htmlFor="loginPassword"
                              className="form-label"
                            >
                              Password<span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type={showLoginPassword ? "text" : "password"}
                              className={`form-control ${
                                loginErrors.password ? "is-invalid" : ""
                              }`}
                              id="loginPassword"
                              name="password"
                              placeholder="Password"
                              value={loginFormData.password}
                              onChange={handleLoginChange}
                            />
                            <span
                              className="position-absolute end-0 top-50 translate-middle-y me-3 mt-3 pt-3"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setShowLoginPassword(!showLoginPassword)
                              }
                            >
                              {showLoginPassword ? (
                                <i className="bi bi-eye-slash"></i>
                              ) : (
                                <i className="bi bi-eye"></i>
                              )}
                            </span>
                            {loginErrors.password && (
                              <div className="invalid-feedback">
                                {loginErrors.password}
                              </div>
                            )}
                          </div>
                          <div className="mb-3 d-flex align-items-center">
                            {/* <input
                              type="text"
                              className={`form-control w-50 ${loginErrors.captcha ? "is-invalid" : ""}`}
                              name="captcha"
                              placeholder="Enter captcha"
                              value={loginFormData.captcha}
                              onChange={handleLoginChange}
                            /> */}
                            {/* <img
                              src={loginCaptchaImage}
                              alt="Captcha"
                              className="ms-2"
                              style={{ cursor: "pointer" }}
                            />
                            <i
                              className="bi bi-arrow-repeat ms-2"
                              style={{ cursor: "pointer", fontSize: "3.2rem" }}
                              onClick={() => {
                                const newCap = generateCaptcha();
                                setCurrentLoginCaptcha(newCap);
                                setLoginCaptchaImage(`https://dummyimage.com/80x35/ccc/000&text=${newCap}`);
                              }}
                              title="Refresh Captcha"
                            ></i> */}
                            <ReCAPTCHA
                              sitekey="6Leyes4rAAAAAHVXttf7gCAHksf2Z7ESEi-5eFag"
                              onChange={handleRecaptchaChange}
                            />
                            {loginErrors.captcha && (
                              <div className="invalid-feedback d-block ms-2">
                                {loginErrors.captcha}
                              </div>
                            )}
                          </div>
                          <div className="d-grid mb-3">
                            <button
                              type="submit"
                              className="btn tf-btn bg-color-primary pd-8 w-100"
                              disabled={loginLoading}
                            >
                              {loginLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  <span className="ms-2">Logging in...</span>
                                </>
                              ) : (
                                "Login"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div id="div2" className={`fade-div ${!showLogin ? "show" : ""}`}>
                <div className="login-box form-contact form">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="p-md-5">
                        <h5 className="mb-4 title text-center">Sign Up</h5>
                        <hr />
                        <label className="form-label">
                          I am<span style={{ color: "red" }}>*</span>
                        </label>
                        <fieldset className="radio-item">
                          <label className="me-4">
                            <span className="text-1">Owner</span>
                            <input
                              type="radio"
                              name="usertype"
                              checked={
                                registerFormData.usertype === UserType.Owner
                              }
                              onChange={() =>
                                handleRegisterUserTypeChange(UserType.Owner)
                              }
                            />
                            <span className="btn-radio"></span>
                          </label>
                          <label className="me-4">
                            <span className="text-1">Agent</span>
                            <input
                              type="radio"
                              name="usertype"
                              checked={
                                registerFormData.usertype === UserType.Agent
                              }
                              onChange={() =>
                                handleRegisterUserTypeChange(UserType.Agent)
                              }
                            />
                            <span className="btn-radio"></span>
                          </label>
                          <label>
                            <span className="text-1">Buyer</span>
                            <input
                              type="radio"
                              name="usertype"
                              checked={
                                registerFormData.usertype === UserType.Buyer
                              }
                              onChange={() =>
                                handleRegisterUserTypeChange(UserType.Buyer)
                              }
                            />
                            <span className="btn-radio"></span>
                          </label>
                        </fieldset>
                        <form onSubmit={handleRegisterSubmit}>
                          <div className="mb-3">
                            <input
                              type="text"
                              className={`form-control ${
                                registerErrors.name ? "is-invalid" : ""
                              }`}
                              name="name"
                              placeholder="Name"
                              value={registerFormData.name}
                              onChange={handleRegisterChange}
                            />
                            {registerErrors.name && (
                              <div className="invalid-feedback">
                                {registerErrors.name}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <input
                              type="email"
                              className={`form-control ${
                                registerErrors.email ? "is-invalid" : ""
                              }`}
                              name="email"
                              placeholder="Email"
                              value={registerFormData.email}
                              onChange={handleRegisterChange}
                            />
                            {registerErrors.email && (
                              <div className="invalid-feedback">
                                {registerErrors.email}
                              </div>
                            )}
                          </div>
                          <div className="mb-3 position-relative">
                            <input
                              type={showRegisterPassword ? "text" : "password"}
                              className={`form-control ${
                                registerErrors.password ? "is-invalid" : ""
                              }`}
                              name="password"
                              placeholder="Password"
                              value={registerFormData.password}
                              onChange={handleRegisterChange}
                            />
                            <span
                              className="position-absolute end-0 top-50 translate-middle-y me-3"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setShowRegisterPassword(!showRegisterPassword)
                              }
                            >
                              {showRegisterPassword ? (
                                <i className="bi bi-eye-slash"></i>
                              ) : (
                                <i className="bi bi-eye"></i>
                              )}
                            </span>
                            {registerErrors.password && (
                              <div className="invalid-feedback">
                                {registerErrors.password}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <input
                              type="text"
                              className={`form-control ${
                                registerErrors.mobile ? "is-invalid" : ""
                              }`}
                              name="mobile"
                              placeholder="10-digit mobile number"
                              value={registerFormData.mobile}
                              onChange={handleRegisterChange}
                            />
                            {registerErrors.mobile && (
                              <div className="invalid-feedback">
                                {registerErrors.mobile}
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <fieldset className="checkbox-item style-1">
                              <label>
                                <span className="text-4">
                                  I agree to MyLandMart <a href="#">T&C</a>,{" "}
                                  <a href="#">Privacy Policy</a>, &{" "}
                                  <a href="#">Cookie Policy</a>
                                </span>
                                <input
                                  type="checkbox"
                                  name="agreeToTerms"
                                  checked={registerFormData.agreeToTerms}
                                  onChange={handleRegisterChange}
                                />
                                <span className="btn-checkbox"></span>
                              </label>
                              {registerErrors.agreeToTerms && (
                                <div className="text-danger mt-1">
                                  {registerErrors.agreeToTerms}
                                </div>
                              )}
                            </fieldset>
                          </div>
                          <div className="mb-3 d-flex align-items-center">
                            {/* <input
                              type="text"
                              className={`form-control w-50 ${
                                registerErrors.captcha ? "is-invalid" : ""
                              }`}
                              name="captcha"
                              placeholder="Enter captcha"
                              value={registerFormData.captcha}
                              onChange={handleRegisterChange}
                            />
                            <img
                              src={registerCaptchaImage}
                              alt="Captcha"
                              className="ms-2"
                              style={{ cursor: "pointer" }}
                            />
                            <i
                              className="bi bi-arrow-repeat ms-2"
                              style={{ cursor: "pointer", fontSize: "3.2rem" }}
                              onClick={() => {
                                const newCap = generateCaptcha();
                                setCurrentRegisterCaptcha(newCap);
                                setRegisterCaptchaImage(
                                  `https://dummyimage.com/80x35/ccc/000&text=${newCap}`
                                );
                              }}
                              title="Refresh Captcha"
                            ></i> */}
                            <ReCAPTCHA
                              sitekey="6Leyes4rAAAAAHVXttf7gCAHksf2Z7ESEi-5eFag"
                              onChange={handleRecaptchaChange}
                            />
                            {registerErrors.captcha && (
                              <div className="invalid-feedback d-block ms-2">
                                {registerErrors.captcha}
                              </div>
                            )}
                          </div>
                          <div className="d-grid mb-3">
                            <button
                              type="submit"
                              className="btn tf-btn bg-color-primary pd-8 w-100"
                              disabled={registerLoading}
                            >
                              {registerLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  <span className="ms-2">Signing Up...</span>
                                </>
                              ) : (
                                "Sign Up"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                    <div className="col-md-6 left-section d-none d-sm-block"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center pb-5">
            <div className="btn-wrapper">
              <button id="toggleBtn" className="slide-btn" onClick={toggleFade}>
                {showLogin
                  ? "New to MyLandMart? Sign Up"
                  : "Already registered? Login"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showOtpModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header p-4">
                <h5 className="modal-title">Verify Your Account</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowOtpModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <p style={{ fontSize: "18px", lineHeight: "25px" }}>
                  An OTP has been sent to your registered email address. Please
                  enter it below to verify your account.
                </p>
                <form onSubmit={handleOtpSubmit}>
                  <div className="mb-3 mt-4 pt-3">
                    <div className="d-flex justify-content-between">
                      {otpDigits.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-digit-${index}`}
                          type="text"
                          className={`form-control text-center mx-2 py-3 ${
                            otpError ? "is-invalid" : ""
                          }`}
                          value={digit}
                          style={{ fontSize: "16px" }}
                          maxLength={1}
                          onChange={(e) =>
                            handleOtpDigitChange(index, e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Backspace" && !digit && index > 0) {
                              const prevInput = document.getElementById(
                                `otp-digit-${index - 1}`
                              );
                              if (prevInput) prevInput.focus();
                            }
                          }}
                        />
                      ))}
                    </div>
                    {otpError && (
                      <div className="invalid-feedback d-block">{otpError}</div>
                    )}
                  </div>
                  <div className="d-grid pt-3">
                    <button
                      type="submit"
                      className="btn tf-btn bg-color-primary pd-8 w-100"
                      disabled={otpLoading}
                    >
                      {otpLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          <span className="ms-2">Verifying...</span>
                        </>
                      ) : (
                        "Verify OTP"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </FrontendLayout>
  );
};

export default LoginRegister;
